import React, { useEffect, useState, useCallback } from 'react';
import {
  AdjustmentsHorizontalIcon, PlusIcon, ArrowDownIcon,
  XMarkIcon, ChevronLeftIcon, ChevronRightIcon,
  PaperAirplaneIcon, PaperClipIcon, DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import apiClient from '../../lib/apiClient';

interface Ticket {
  id: string;
  subject: string;
  description: string;
  user_name: string | null;
  user_email: string | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  assigned_to: string | null;
  internal_note: string | null;
  admin_reply: string | null;
  replied_at: string | null;
  task_id: string | null;
  task_company: string | null;
  created_at: string;
  user?: { id: number; name: string; email: string };
}

interface Stats { total: number; open: number; in_progress: number; resolved: number }
interface Pagination { total: number; current_page: number; per_page: number; last_page: number }

const PRIORITY_COLOR: Record<string, string> = {
  urgent: 'bg-red-50 text-red-600',
  high:   'bg-amber-50 text-amber-600',
  medium: 'bg-purple-50 text-purple-700',
  low:    'bg-slate-100 text-slate-500',
};
const STATUS_DOT: Record<string, string> = {
  open: 'bg-amber-400', in_progress: 'bg-brand-500', resolved: 'bg-emerald-500', closed: 'bg-slate-400',
};

export default function SupportTickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [note, setNote] = useState('');
  const [reply, setReply] = useState('');
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [selectedJobRefTicket, setSelectedJobRefTicket] = useState<Ticket | null>(null);

  const fetchTickets = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = { page };
    if (statusFilter) params.status = statusFilter;
    if (priorityFilter) params.priority = priorityFilter;
    apiClient.get('/admin/support-tickets', { params })
      .then(({ data }) => {
        setTickets(data.data?.data ?? data.data ?? []);
        setStats(data.stats);
        const pg = data.data;
        setPagination({ total: pg.total, current_page: pg.current_page, per_page: pg.per_page, last_page: pg.last_page });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, statusFilter, priorityFilter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const updateStatus = async (ticketId: string, status: string) => {
    setSaving(true);
    await apiClient.patch(`/admin/support-tickets/${ticketId}`, { status })
      .then(() => { fetchTickets(); if (selectedTicket?.id === ticketId) setSelectedTicket(t => t ? { ...t, status: status as any } : t); })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const saveNote = async () => {
    if (!selectedTicket || !note.trim()) return;
    setSaving(true);
    await apiClient.patch(`/admin/support-tickets/${selectedTicket.id}`, { internal_note: note, status: 'in_progress' })
      .then(() => { fetchTickets(); setSelectedTicket(t => t ? { ...t, internal_note: note, status: 'in_progress' } : t); })
      .finally(() => setSaving(false));
  };

  const sendReply = async () => {
    if (!selectedTicket || !reply.trim()) return;
    setSaving(true);
    await apiClient.patch(`/admin/support-tickets/${selectedTicket.id}`, {
      admin_reply: reply,
      status: 'resolved',
      replied_at: new Date().toISOString(),
    })
      .then(() => {
        fetchTickets();
        setSelectedTicket(t => t ? { ...t, admin_reply: reply, status: 'resolved' } : t);
        setReply('');
        setAttachments([]);
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1400px] mx-auto flex flex-col gap-6 pb-20">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-extrabold text-slate-900 tracking-tight">Support Tickets</h1>
          <p className="text-[13px] text-slate-500 mt-1">Manage and resolve incoming customer enquiries and technical issues.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="bg-white border border-slate-200 text-[13px] font-bold text-slate-700 px-3 py-2 rounded-xl focus:outline-none appearance-none cursor-pointer pr-7 relative">
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}
              className="bg-white border border-slate-200 text-[13px] font-bold text-slate-700 px-3 py-2 rounded-xl focus:outline-none appearance-none cursor-pointer">
              <option value="">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Total',       value: stats?.total,       color: 'text-slate-900' },
          { label: 'Open',        value: stats?.open,        color: 'text-amber-600' },
          { label: 'In Progress', value: stats?.in_progress, color: 'text-brand-600' },
          { label: 'Resolved',    value: stats?.resolved,    color: 'text-emerald-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">{s.label} Tickets</p>
            {loading ? <div className="h-8 w-16 bg-slate-100 animate-pulse rounded" /> :
              <p className={`text-xl sm:text-[32px] font-extrabold leading-none break-all ${s.color}`}>{s.value ?? 0}</p>}
          </div>
        ))}
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

        {/* Ticket List */}
        <div className="xl:col-span-7 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden">
          <div className="overflow-x-auto flex flex-col flex-1">
          <div className="grid grid-cols-12 px-6 py-4 bg-slate-50/50 border-b border-slate-100 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest min-w-[560px]">
            <div className="col-span-2">ID</div>
            <div className="col-span-3">Subject</div>
            <div className="col-span-2">Job Reference</div>
            <div className="col-span-2">Priority</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Date</div>
          </div>

          <div className="flex flex-col divide-y divide-slate-100 flex-1 min-w-[560px]">
            {loading ? (
              Array.from({length:5}).map((_,i) => (
                <div key={i} className="grid grid-cols-12 items-center px-6 py-5">
                  {[2,3,2,2,2,1].map((w,j) => (
                    <div key={j} className={`col-span-${w} pr-4`}><div className="h-4 bg-slate-100 animate-pulse rounded" /></div>
                  ))}
                </div>
              ))
            ) : tickets.length === 0 ? (
              <div className="px-6 py-12 text-center text-slate-400 font-medium">No tickets found.</div>
            ) : tickets.map(t => (
              <div
                key={t.id}
                onClick={() => { setSelectedTicket(t); setNote(t.internal_note ?? ''); setReply(''); }}
                className={`grid grid-cols-12 items-center px-6 py-5 cursor-pointer transition-colors ${
                  selectedTicket?.id === t.id
                    ? 'bg-brand-50/40 border-l-4 border-brand-500'
                    : 'hover:bg-slate-50 border-l-4 border-transparent'
                }`}
              >
                <div className="col-span-2 text-[12px] font-mono text-slate-500">
                  #{t.id.substring(0, 8)}
                </div>
                <div className="col-span-3 pr-3">
                  <p className="text-[13px] font-extrabold text-slate-900 leading-tight truncate">{t.subject}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{t.user_name ?? t.user?.name ?? 'Anonymous'}</p>
                </div>
                <div className="col-span-2">
                  {t.task_id ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedJobRefTicket(t);
                      }}
                      className="flex items-center gap-1.5 text-[11px] font-mono text-brand-600 hover:text-brand-700 hover:bg-brand-50 px-2 py-1 rounded-lg transition-colors"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4 shrink-0" />
                      <span className="truncate">View Job</span>
                    </button>
                  ) : (
                    <span className="text-[11px] font-mono text-slate-400">—</span>
                  )}
                </div>
                <div className="col-span-2">
                  <span className={`px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-widest rounded-full ${PRIORITY_COLOR[t.priority]}`}>{t.priority}</span>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${STATUS_DOT[t.status]}`} />
                  <span className="text-[12px] font-bold text-slate-700 capitalize">{t.status.replace('_', ' ')}</span>
                </div>
                <div className="col-span-2 text-right text-[11px] text-slate-400">
                  {new Date(t.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>

          </div>
          {/* Pagination */}
          {pagination && pagination.last_page > 1 && (
            <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/30">
              <span className="text-[12px] text-slate-400">Page {pagination.current_page} of {pagination.last_page}</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={pagination.current_page===1}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors">
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <button onClick={() => setPage(p => Math.min(pagination.last_page, p+1))} disabled={pagination.current_page===pagination.last_page}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors">
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Ticket Detail / Reply Panel */}
        <div className="xl:col-span-5 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col p-6">
          {!selectedTicket ? (
            <div className="flex-1 flex items-center justify-center text-center text-slate-400">
              <div>
                <ExclamationTriangleIcon className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                <p className="font-medium text-[14px]">Select a ticket to view details</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-[16px] font-extrabold text-slate-900 truncate pr-4">{selectedTicket.subject}</h3>
                <button onClick={() => setSelectedTicket(null)} className="text-slate-400 hover:text-slate-700 shrink-0">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className={`px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-widest rounded-full ${PRIORITY_COLOR[selectedTicket.priority]}`}>{selectedTicket.priority}</span>
                <span className="text-[12px] text-slate-500 font-medium">{selectedTicket.user_name ?? selectedTicket.user?.name ?? 'Anonymous'}</span>
                <span className="text-[12px] text-slate-400">{selectedTicket.user_email ?? selectedTicket.user?.email ?? ''}</span>
              </div>

              <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 mb-4">
                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-1.5">Description</p>
                <p className="text-[13px] text-slate-700 leading-relaxed">{selectedTicket.description}</p>
              </div>

              {/* Status Update */}
              <div className="mb-4">
                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Update Status</p>
                <div className="flex gap-2 flex-wrap">
                  {(['open','in_progress','resolved','closed'] as const).map(s => (
                    <button key={s} disabled={saving || selectedTicket.status===s}
                      onClick={() => updateStatus(selectedTicket.id, s)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-extrabold uppercase tracking-wider transition-colors ${
                        selectedTicket.status === s
                          ? 'bg-brand-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}>
                      {s.replace('_',' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assign */}
              <div className="flex gap-2 mb-4">
                <button className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 py-2.5 rounded-xl text-[12px] font-extrabold transition-colors">
                  Assign to Me
                </button>
                <button onClick={() => updateStatus(selectedTicket.id, 'in_progress')}
                  className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 py-2.5 rounded-xl text-[12px] font-extrabold transition-colors">
                  Escalate
                </button>
              </div>

              {/* Internal Note */}
              <div className="mb-4">
                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Internal Note (Private)</p>
                <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Add a private note..."
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl p-3 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 resize-none" />
                <button onClick={saveNote} disabled={saving || !note.trim()}
                  className="mt-1.5 text-[11px] font-bold text-brand-600 hover:text-slate-700 disabled:opacity-40">
                  {saving ? 'Saving...' : 'Save Note'}
                </button>
              </div>

              {/* Show Previous Reply if exists */}
              {selectedTicket.admin_reply && (
                <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <p className="text-[10px] font-extrabold text-emerald-600 uppercase tracking-widest mb-2">Your Reply (Sent)</p>
                  <p className="text-[13px] text-emerald-900 whitespace-pre-wrap break-words leading-relaxed">{selectedTicket.admin_reply}</p>
                  {selectedTicket.replied_at && (
                    <p className="text-[10px] text-emerald-600 mt-2 font-medium">
                      {new Date(selectedTicket.replied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              )}

              {/* Reply */}
              {!selectedTicket.admin_reply && (
              <div className="flex-1 flex flex-col">
                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">
                  Reply to {selectedTicket.user_name ?? 'Customer'}
                </p>
                <div className="border border-slate-200 rounded-xl overflow-hidden focus-within:border-brand-300 focus-within:ring-2 focus-within:ring-brand-500/20 transition-all bg-white flex flex-col flex-1">
                  <textarea rows={4} value={reply} onChange={e => setReply(e.target.value)}
                    placeholder="Write your response..."
                    className="w-full bg-transparent border-none p-4 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none resize-none flex-1" />
                  {attachments.length > 0 && (
                    <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 flex flex-wrap gap-2">
                      {attachments.map((file, idx) => (
                        <div key={idx} className="bg-white px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-700 border border-slate-100 flex items-center gap-2">
                          {file.name}
                          <button onClick={() => setAttachments(a => a.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="px-4 py-2 border-t border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <label className="text-slate-400 hover:text-slate-600 cursor-pointer">
                      <PaperClipIcon className="w-5 h-5" />
                      <input type="file" multiple hidden onChange={e => setAttachments([...attachments, ...Array.from(e.target.files || [])])} />
                    </label>
                    <span className="text-[10px] text-slate-400">{attachments.length} file(s)</span>
                  </div>
                </div>
                <button
                  onClick={sendReply}
                  disabled={saving || !reply.trim()}
                  className="mt-3 w-full bg-brand-500 text-white py-3 rounded-xl text-[13px] font-bold flex items-center justify-center gap-2 hover:bg-brand-600 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Sending...' : 'Send Reply'} <PaperAirplaneIcon className="w-4 h-4" />
                </button>
              </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bottom Metrics */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Avg Response Time', value: '—', sub: 'Calculated from ticket history' },
          { label: 'CSAT Score',        value: '—', sub: 'Customer satisfaction' },
          { label: 'Resolved MTD',      value: stats?.resolved ?? 0, sub: 'This period' },
          { label: 'Pending',           value: (stats?.open ?? 0) + (stats?.in_progress ?? 0), sub: 'Need attention', accent: true },
        ].map(m => (
          <div key={m.label} className={`rounded-2xl p-5 border shadow-sm ${m.accent ? 'bg-brand-50 border-brand-100' : 'bg-white border-slate-200'}`}>
            <p className={`text-[10px] font-extrabold uppercase tracking-widest mb-2 ${m.accent ? 'text-brand-600' : 'text-slate-400'}`}>{m.label}</p>
            <p className={`text-[32px] font-extrabold leading-none ${m.accent ? 'text-brand-600' : 'text-slate-900'}`}>
              {loading ? '—' : m.value}
            </p>
            {m.sub && <p className={`text-[11px] mt-1 font-medium ${m.accent ? 'text-brand-500' : 'text-slate-400'}`}>{m.sub}</p>}
          </div>
        ))}
      </div>

      {/* Job Reference Modal */}
      {selectedJobRefTicket && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-[16px] font-extrabold text-slate-900">Job Reference Details</h2>
              <button
                onClick={() => setSelectedJobRefTicket(null)}
                className="text-slate-400 hover:text-slate-700 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Job ID</p>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 font-mono text-[14px] text-slate-900 break-all">
                  {selectedJobRefTicket.task_id}
                </div>
              </div>
              {selectedJobRefTicket.task_company && (
                <div>
                  <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Company</p>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-[14px] text-slate-900">
                    {selectedJobRefTicket.task_company}
                  </div>
                </div>
              )}
              <div>
                <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Related Ticket</p>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-[12px] font-semibold text-slate-900">{selectedJobRefTicket.subject}</p>
                  <p className="text-[11px] text-slate-500 mt-1">Ticket ID: #{selectedJobRefTicket.id.substring(0, 8)}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-2">
              <button
                onClick={() => setSelectedJobRefTicket(null)}
                className="flex-1 px-4 py-2.5 bg-brand-600 text-white text-[13px] font-bold rounded-xl hover:bg-brand-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
